import { z } from "zod";
export const PriviligesSchema = z.object({
    editAppSettings: z.boolean().default(false),
    editAppPermissions: z.boolean().default(false),
    // `manageRooms` and `grantAccess` both let a user join a gated room (private,
    // or a scheduled room outside its window) directly AND approve/deny others'
    // knock-to-join requests for such rooms. `manageRooms` is the more powerful
    // role (it also creates/updates/closes rooms and opens rooms on demand);
    // `grantAccess` is the narrower "doorkeeper" that can admit waiting users and
    // access any room without managing rooms. `joinAnyRoom` only skips the gate:
    // its holders are not approvers. See `bypassesJoinGate` and the join gate in
    // `src/routes/rooms/joinRoom.ts`.
    manageRooms: z.boolean().default(false),
    closeRoom: z.boolean().default(false),
    openChildRoom: z.boolean().default(false),
    closeChildRoom: z.boolean().default(false),
    makeRoomPrivate: z.boolean().default(false),
    grantAccess: z.boolean().default(false),
    joinAnyRoom: z.boolean().default(false),
    muteUser: z.boolean().default(false),
    pokeUser: z.boolean().default(false),
    kickUser: z.boolean().default(false),
    useMicrophone: z.boolean().default(false),
    useCamera: z.boolean().default(false),
    clearTextChat: z.boolean().default(false),
    clearDrawings: z.boolean().default(false),
    startRecording: z.boolean().default(false),
    grantTalkPermission: z.boolean().default(false),
    // Upload files to the room and delete any shared file. Everyone in a room
    // can list and download its files. Stored roles from before this privilege
    // existed inherit `editAppPermissions` (see the server's stored-role parser).
    shareFiles: z.boolean().default(false),
});
/**
 * Privileges a default role must never hold. The default role is what every
 * anonymous visitor is granted, so administrative privileges on it would hand
 * app control to the public. Enforced on every write path that can set a
 * default role's privileges (`appSetup`, `setRole`, `transferDefaultRole`) and
 * cleaned from legacy data by migration 003. Shared by the server and UI.
 */
export const DEFAULT_ROLE_FORBIDDEN_PRIVILEGES = [
    "editAppPermissions",
    "editAppSettings",
];
/**
 * Privileges that let a user join a gated room (private, or scheduled and
 * outside its window) without knocking. Only `manageRooms` and `grantAccess`
 * also make the holder an approver of other users' knocks.
 */
export const JOIN_GATE_BYPASS_PRIVILEGES = [
    "manageRooms",
    "grantAccess",
    "joinAnyRoom",
];
/** Whether `privileges` let the user skip the join gate (see above). */
export function bypassesJoinGate(privileges) {
    return JOIN_GATE_BYPASS_PRIVILEGES.some((id) => privileges?.[id] === true);
}
/** The forbidden-for-default privileges a role currently has enabled (empty = OK as default). */
export function forbiddenDefaultPrivileges(privileges) {
    return DEFAULT_ROLE_FORBIDDEN_PRIVILEGES.filter((id) => privileges[id] === true);
}
/**
 * Body of `POST /v1/permissions/default-role`. When a previous default role
 * exists (and differs from `roleId`), `previousRole` is required: it must be
 * given a real password or be deleted — otherwise it would stay reachable via
 * whatever throwaway password it carried while it was the default.
 */
export const DefaultRoleTransferSchema = z.object({
    roleId: z.string().max(256),
    previousRole: z.union([
        z.object({ password: z.string().min(6).max(256) }),
        z.object({ delete: z.literal(true) }),
    ]).optional(),
});
/** Common role command fields; password is an input, never a public response field. */
export const RoleInputSchema = z.object({
    name: z.string().min(3).max(256),
    password: z.string().min(6).max(256).optional(),
    privileges: PriviligesSchema,
});
export const RoleUpdateSchema = RoleInputSchema.extend({
    default: z.boolean().optional(),
});
export const RoleNoPasswordSchema = RoleInputSchema.omit({ password: true })
    .extend({
    id: z.string().max(256),
    default: z.boolean(),
    isMember: z.boolean().optional(),
});
export const RolesNoPasswordSchema = z.record(z.string(), RoleNoPasswordSchema);
export const DefaultRoleTransferResultSchema = z.object({
    defaultRole: z.string(),
    changed: z.boolean(),
    evicted: z.number(),
    previousRole: z.object({
        id: z.string(),
        action: z.enum(["password-set", "deleted"]),
    }).nullable(),
});
